import { useLayoutEffect, useRef } from 'react';

/**
 * usePosterFit — auto-fits poster typography to a fixed-ratio (A4) sheet.
 *
 * The poster box keeps a fixed A4 aspect ratio while its content height depends
 * on the job's text. Every font/icon/vertical-spacing size in the poster is
 * written as `calc(<n>cqw * var(--fs, 1))`, so this hook binary-searches the
 * `--fs` multiplier until the naturally-flowing content exactly fills the
 * sheet: long content shrinks, short content grows — no dead space at the
 * bottom when the sheet is printed on A4 paper.
 *
 * Refs: `boxRef` = the fixed-ratio sheet, `innerRef` = the flowing column
 * measured against the sheet height, `contentRef` = the flex-1 body that is
 * released during measurement so it reports its natural (unstretched) height.
 */

const MIN_FS = 0.55;
const MAX_FS = 1.5;

export default function usePosterFit(deps) {
    const boxRef = useRef(null);
    const innerRef = useRef(null);
    const contentRef = useRef(null);

    useLayoutEffect(() => {
        const box = boxRef.current;
        const inner = innerRef.current;
        if (!box || !inner) return undefined;

        let lastW = -1;
        let lastH = -1;
        const fit = () => {
            const target = box.clientHeight;
            if (!target) return; // detached / display:none — nothing to fit
            lastW = box.clientWidth;
            lastH = target;
            const content = contentRef.current;
            // Release the stretch (min-h-full + flex-1) so offsetHeight is the
            // content's natural height at the probed scale.
            inner.style.minHeight = '0';
            if (content) content.style.flex = 'none';
            const heightAt = (fs) => {
                box.style.setProperty('--fs', fs);
                return inner.offsetHeight;
            };
            let fs;
            if (heightAt(MAX_FS) <= target) {
                fs = MAX_FS;
            } else if (heightAt(MIN_FS) >= target) {
                fs = MIN_FS;
            } else {
                let lo = MIN_FS;
                let hi = MAX_FS;
                for (let i = 0; i < 8; i += 1) {
                    const mid = (lo + hi) / 2;
                    if (heightAt(mid) <= target) lo = mid;
                    else hi = mid;
                }
                fs = lo; // largest probed scale that still fits
            }
            box.style.setProperty('--fs', fs);
            inner.style.minHeight = '';
            if (content) content.style.flex = '';
        };

        fit();
        // Bengali webfont metrics land late — refit once fonts are ready (only when some are still loading: measuring text is what starts
        // a font's download, so right after fit() the status says whether any are on their way).
        if (document.fonts?.ready && document.fonts.status === 'loading') document.fonts.ready.then(fit).catch(() => {});
        // The observer reports once straight after observe(); a refit is only needed when the sheet's size really differs from the fitted one.
        const ro = new ResizeObserver(() => {
            if (box.clientWidth === lastW && box.clientHeight === lastH) return;
            fit();
        });
        ro.observe(box);
        return () => ro.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    return { boxRef, innerRef, contentRef };
}
